import React, { useState, useRef, useEffect, useContext } from "react";

//Import Mui
import {
  Box,
  Button,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import MuiTextField from "../../controls/MuiTextField";
import MuiAutoComplete from "../../controls/MuiAutoComplete";
import MuiDatePicker from "../../controls/MuiDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Alert from "../../controls/MuiSnackbarAlert";

//Import Redux
import { useDispatch, useSelector } from "react-redux";

//Import Reduxtoolkit
import { unwrapResult } from "@reduxjs/toolkit";

//Import from customerSlice.js
import {
  getCustomersFromServer,
  selectAllCustomers,
} from "../../masters/customer/customerSlice";

//Import from salesReportSlice.js
import {
  getSalesDetailsforSalesReportFromServer,
  getSalesDetailsforSalesReportThermalPrintFromServer,
} from "./salesReportSlice";

//Import ReactToPrint
import { useReactToPrint } from "react-to-print";

//Import Pages
import SalesReportThermalPrint from "./SalesReportThermalPrint";

//Import CSS
import "./salesReport.css";

const SalesReport = () => {
  const dispatch = useDispatch();
  const customersList = useSelector(selectAllCustomers);

  //useState
  const [selectedFromDate, setSelectedFromDate] = useState(dayjs());
  const [isFromDatePickerOpen, setIsFromDatePickerOpen] = useState(false);
  const [selectedToDate, setSelectedToDate] = useState(dayjs());
  const [isToDatePickerOpen, setIsToDatePickerOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerCode, setCustomerCode] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [tableData, setTableData] = useState([]);
  const [salesItemCount, setSalesItemCount] = useState(0);
  const [thermalPrintSalesDetails, setThermalPrintSalesDetails] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    severity: "",
  });
  const [showChild, setShowChild] = useState(false);
  const [printReady, setPrintReady] = useState(false);
  //End useState

  //useRef
  const customersRef = useRef(null);
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);
  const searchRef = useRef(null);
  const printRef = useRef(null);
  const refreshRef = useRef(null);
  //End useRef

  //useEffect
  useEffect(() => {
    // if (customersList !== undefined && customersList.length > 0) {
    //   setCustomers(customersList);
    // } else {
    dispatch(getCustomersFromServer())
      .then(unwrapResult)
      .then((result) => {
        if (result.data.length > 0) {
          setCustomers(result.data);
        }
      })
      .catch((err) => {
        console.log({ ErrorOccured: err });
        setNotify({
          isOpen: true,
          message: err.message,
          severity: "error",
        });
      });
    // }
  }, []);

  useEffect(() => {
    if (showChild && printReady) {
      handlePrintClick();
      setPrintReady(false); // Reset printReady after printing
    }
  }, [showChild, printReady]);
  //End useEffect

  const handleFromDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      setSelectedFromDate(newValue);
      setIsFromDatePickerOpen(false);
    }
  };

  const handleFromDateBlur = (event) => {
    const inputValue = event.target.value;
    const parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
    if (!parsedDate.isValid()) {
      // This is required
      // setError({
      //   salesDate: "Enter a valid date in DD/MM/YYYY format",
      //   customerName: "",
      //   itemName: "",
      //   quantity: "",
      //   uomName: "",
      //   unitPrice: "",
      // });
      setSelectedFromDate(dayjs());
      setIsFromDatePickerOpen(false);
    } else {
      // This is required
      // setError({ salesDate: "" }); // Clear error if date is valid
      setSelectedFromDate(parsedDate);
    }
  };

  const handleToDateChange = (newValue) => {
    if (newValue && newValue.isValid()) {
      setSelectedToDate(newValue);
      setIsToDatePickerOpen(false);
    }
  };

  const handleToDateBlur = (event) => {
    const inputValue = event.target.value;
    const parsedDate = dayjs(inputValue, "DD/MM/YYYY", true);
    if (!parsedDate.isValid()) {
      // This is required
      // setError({
      //   salesDate: "Enter a valid date in DD/MM/YYYY format",
      //   customerName: "",
      //   itemName: "",
      //   quantity: "",
      //   uomName: "",
      //   unitPrice: "",
      // });
      setSelectedToDate(dayjs());
      setIsToDatePickerOpen(false);
    } else {
      // This is required
      // setError({ salesDate: "" }); // Clear error if date is valid
      setSelectedToDate(parsedDate);
    }
  };

  const handleCustomerChange = async (e, value) => {
    e.preventDefault();
    setSelectedCustomer(value);
    if (value) {
      setCustomerCode(value.CUCode);
      setCustomerName(value.CUName);
      // Remove commas from the OpeningBalance string and convert it to a number
      // const cleanOpeningBalance = parseFloat(
      //   value.OpeningBalance.replace(/,/g, "")
      // );

      fromDateRef.current.focus();
    }
  };

  const columns = [
    {
      id: "SalesNo",
      label: "Sales No",
      minWidth: 70,
    },
    { id: "SalesDate", label: "Date", minWidth: 100 },
    { id: "IName", label: "Item", minWidth: 170 },
    { id: "Quantity", label: "Quantity", minWidth: 100 },
    { id: "UnitPrice", label: "Price", align: "right", minWidth: 100 },
    {
      id: "TotalAmount",
      label: "Total Amount",
      align: "right",
      minWidth: 120,
    },
    {
      id: "CurrentAmount",
      label: "Current Amount",
      align: "right",
      minWidth: 120,
    },
    {
      id: "LessAmount",
      label: "Less Amount",
      align: "right",
      minWidth: 120,
    },
    {
      id: "NetAmount",
      label: "Net Amount",
      align: "right",
      minWidth: 120,
    },
  ];

  const handleSearch = async () => {
    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else if (selectedFromDate === null) {
      setNotify({
        isOpen: true,
        message: "From Date is required",
        severity: "warning",
      });
      fromDateRef.current.focus();
      return;
    } else if (selectedToDate === null) {
      setNotify({
        isOpen: true,
        message: "To Date is required",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (selectedToDate.isBefore(selectedFromDate)) {
      setNotify({
        isOpen: true,
        message: "To Date should not be earlier than From Date",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else {
      const formatedFromDate = dayjs(selectedFromDate).format("DD/MM/YYYY");
      const formatedToDate = dayjs(selectedToDate).format("DD/MM/YYYY");
      let record = {
        customerCode,
        selectedFromDate: formatedFromDate,
        selectedToDate: formatedToDate,
      };
      await dispatch(getSalesDetailsforSalesReportFromServer(record))
        .then(unwrapResult)
        .then((result) => {
          setSalesItemCount(result.data.length);
          if (result.data.length === 0) {
            setNotify({
              isOpen: true,
              message: result.message,
              severity: "warning",
            });
          } else {
            // Flatten the data for the table
            let flattenedData = [];
            result.data.forEach((sale) => {
              sale.SalesDetails.forEach((detail, index) => {
                flattenedData.push({
                  key: `${sale.SalesNo}-${index}`, // unique key for each row
                  SalesNo: sale.SalesNo,
                  SalesDate: sale.SalesDate,
                  OpeningBalance: sale.OpeningBalance,
                  IName: detail.IName,
                  Quantity: detail.Quantity,
                  UnitPrice: detail.UnitPrice,
                  TotalAmount: detail.TotalAmount,
                  // Assuming CurrentAmount, LessAmount, NetAmount are calculated or need to be set
                  CurrentAmount: sale.CurrentAmount, // Set actual value if available
                  LessAmount: sale.LessAmount, // Set actual value if available
                  NetAmount: sale.NetAmount, // Set actual value if available
                });
              });
            });

            // Preprocess data to calculate row spans
            let salesRowSpanData = {};
            flattenedData.forEach((row, index) => {
              if (salesRowSpanData[row.SalesNo]) {
                salesRowSpanData[row.SalesNo].count += 1;
              } else {
                salesRowSpanData[row.SalesNo] = { count: 1, index };
              }
            });

            const processedData = flattenedData.map((row, index) => {
              // console.log(row); => 0
              // console.log(index); => 0
              // console.log(salesRowSpanData[row.SalesNo].index); => 0
              // console.log(salesRowSpanData[row.SalesNo].count); => 2
              if (salesRowSpanData[row.SalesNo].index === index) {
                return { ...row, rowSpan: salesRowSpanData[row.SalesNo].count };
              }
              return { ...row, rowSpan: 0 };
            });
            setTableData(processedData);
            setThermalPrintSalesDetails([]);
            setShowChild(false);
            setPrintReady(false);
          }
        })
        .catch((err) => {
          console.log({ ErrorOccured: err });
          setNotify({
            isOpen: true,
            message: err.message,
            severity: "error",
          });
        });
    }
  };

  const handlePrintClick = useReactToPrint({
    content: () => printRef.current,
  });

  const handlePrint = async (e) => {
    e.preventDefault();

    if (selectedCustomer === null) {
      setNotify({
        isOpen: true,
        message: "Customer is required",
        severity: "warning",
      });
      customersRef.current.focus();
      return;
    } else if (selectedFromDate === null) {
      setNotify({
        isOpen: true,
        message: "From date is required",
        severity: "warning",
      });
      fromDateRef.current.focus();
      return;
    } else if (selectedToDate === null) {
      setNotify({
        isOpen: true,
        message: "To Date is required",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (selectedToDate.isBefore(selectedFromDate)) {
      setNotify({
        isOpen: true,
        message: "To Date should not be earlier than From Date",
        severity: "warning",
      });
      toDateRef.current.focus();
      return;
    } else if (tableData && tableData.length === 0) {
      setNotify({
        isOpen: true,
        message: "Sales details not found, please click on Search",
        severity: "warning",
      });
      return;
    } else {
      const formatedFromDate = dayjs(selectedFromDate).format("DD/MM/YYYY");
      const formatedToDate = dayjs(selectedToDate).format("DD/MM/YYYY");
      let record = {
        customerCode,
        selectedFromDate: formatedFromDate,
        selectedToDate: formatedToDate,
      };

      await dispatch(
        getSalesDetailsforSalesReportThermalPrintFromServer(record)
      )
        .then(unwrapResult)
        .then((result) => {
          let flattenedData = [];
          result.data.forEach((sale) => {
            sale.SalesDetails.forEach((detail, index) => {
              flattenedData.push({
                SalesNo: sale.SalesNo,
                SalesDate: sale.SalesDate,
                SalesDay: sale.SalesDay,
                CustomerCode: sale.CustomerCode,
                CustomerName: sale.CustomerName,
                IName: detail.IName,
                Quantity: detail.Quantity,
                UnitPrice: detail.UnitPrice,
                TotalAmount: detail.TotalAmount,
                // Assuming CurrentAmount, LessAmount, NetAmount are calculated or need to be set
                CurrentAmount: sale.CurrentAmount, // Set actual value if available
                SumofCurrentAmount: sale.SumOfCurrentAmount,
                OpeningBalance: sale.OpeningBalance,
                CollectionAmount: sale.CollectionAmount,
              });
            });
          });
          setThermalPrintSalesDetails(flattenedData);
          setShowChild(true);
          setPrintReady(true);
        })
        .catch((err) => {
          console.log({ ErrorOccured: err });
          setNotify({
            isOpen: true,
            message: err.message,
            severity: "error",
          });
        });
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleChangePage = (event, newPage) => {
    event.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  return (
    <>
      <Alert notify={notify} setNotify={setNotify} />
      <Box className="salesreportboxcontainer">
        <Paper className="paper" elevation={2} sx={{ p: 2 }}>
          {/* <h6>Add Customer</h6>
            <Divider /> */}
          <CardContent>
            <Grid container spacing={2} className="gridContainer">
              <Grid item xs={12} sm={6} md={3}>
                <MuiAutoComplete
                  id="customer"
                  clearOnEscape
                  autoHighlight
                  autoSelect
                  options={customers}
                  getOptionLabel={(option) =>
                    `${option.CUCode} - ${option.CUName}` || ""
                  }
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  renderInput={(params) => (
                    <MuiTextField
                      {...params}
                      label="Customer *"
                      inputRef={customersRef}
                      autoFocus
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MuiDatePicker
                    label="From Date *"
                    value={selectedFromDate}
                    onChange={handleFromDateChange}
                    open={isFromDatePickerOpen}
                    onClose={() => setIsFromDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        onClick: (e) => {
                          e.stopPropagation();
                          setIsFromDatePickerOpen(true);
                        },
                        onBlur: handleFromDateBlur,
                        inputRef: fromDateRef,
                        onKeyDown: (e) => {
                          if (e.key.toLowerCase() === "home")
                            customersRef.current.focus();
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MuiDatePicker
                    label="To Date *"
                    value={selectedToDate}
                    onChange={handleToDateChange}
                    open={isToDatePickerOpen}
                    onClose={() => setIsToDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        onClick: (e) => {
                          e.stopPropagation();
                          setIsToDatePickerOpen(true);
                        },
                        onBlur: handleToDateBlur,
                        inputRef: toDateRef,
                        onKeyDown: (e) => {
                          if (e.key.toLowerCase() === "home")
                            fromDateRef.current.focus();
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnsearch"
                  variant="contained"
                  size="small"
                  ref={searchRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      toDateRef.current.focus();
                  }}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  variant="contained"
                  size="small"
                  className="btnprint"
                  ref={printRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      searchRef.current.focus();
                  }}
                  onClick={handlePrint}
                >
                  Print
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
                <Button
                  className="btnrefresh"
                  variant="contained"
                  size="small"
                  ref={refreshRef}
                  onKeyDown={(e) => {
                    if (e.key.toLowerCase() === "home")
                      printRef.current.focus();
                  }}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </Grid>
              <Grid item xs={12}></Grid>

              <TableContainer sx={{ maxHeight: 1000 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      {columns
                        // .filter((column) => !column.hideable)
                        .map((column) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            style={{ minWidth: column.minWidth }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(tableData) && tableData.length > 0 ? (
                      tableData.map((row, index) => (
                        <TableRow key={row.key}>
                          {/* SalesNo and SalesDate */}
                          {row.rowSpan > 0 ? (
                            <>
                              <TableCell rowSpan={row.rowSpan}>
                                {row.SalesNo}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan}>
                                {row.SalesDate}
                              </TableCell>
                            </>
                          ) : null}

                          {/* Item, Quantity, UnitPrice, TotalAmount */}
                          <TableCell>{row.IName}</TableCell>
                          <TableCell>{row.Quantity}</TableCell>
                          <TableCell align="right">{row.UnitPrice}</TableCell>
                          <TableCell align="right">{row.TotalAmount}</TableCell>

                          {/* CurrentAmount, LessAmount, NetAmount */}
                          {row.rowSpan > 0 ? (
                            <>
                              <TableCell rowSpan={row.rowSpan} align="right">
                                {row.CurrentAmount}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan} align="right">
                                {row.LessAmount}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan} align="right">
                                {row.NetAmount}
                              </TableCell>
                            </>
                          ) : null}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} align="center">
                          No Items found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                className="MuiTablePagination-selectLabel"
                rowsPerPageOptions={[10, 25, 50, 75, 100]}
                component="div"
                count={salesItemCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </CardContent>
        </Paper>
      </Box>
      <div style={{ display: "none" }}>
        {showChild && (
          <SalesReportThermalPrint
            salesRecord={thermalPrintSalesDetails}
            ref={printRef}
          />
        )}
      </div>
    </>
  );
};

export default SalesReport;
